import { type MutableRefObject, useEffect, useState } from 'react';

type UseActiveCardObserverProps = {
  cardRefs: MutableRefObject<(HTMLElement | null)[]>;
  childCount: number;
  options: {
    isEnabled: boolean;
    threshold?: number;
  };
};

export const useActiveCardObserver = ({
  cardRefs,
  childCount,
  options,
}: UseActiveCardObserverProps): number => {
  const { threshold = 0.5, isEnabled } = options;
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  useEffect(() => {
    // If this hook is disabled (e.g., on mobile), do nothing.
    if (!isEnabled) {
      setActiveCardIndex(0);
      return;
    }

    const cardElements = cardRefs.current.slice(0, childCount);

    if (cardElements.length === 0) {
      return;
    }

    // Track intersection ratios for all cards to find the most visible one.
    const intersectionRatios = new Map<Element, number>();

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // Update intersection ratios for all observed entries
      for (const entry of entries) {
        intersectionRatios.set(entry.target, entry.intersectionRatio);
      }

      // If the card is bigger than the viewport, the threshold might never be met.
      // So, find the card with the highest intersection ratio that meets the threshold
      // or fallback to the most visible card if none meet the threshold.
      let bestIndex = -1;
      let bestRatio = 0;
      let bestThresholdIndex = -1;
      let bestThresholdRatio = 0;

      for (let i = 0; i < cardElements.length; i++) {
        const element = cardElements[i];
        if (!element) continue;
        const ratio = intersectionRatios.get(element) ?? 0;

        // Track the best card meeting the threshold
        if (ratio >= threshold && ratio > bestThresholdRatio) {
          bestThresholdRatio = ratio;
          bestThresholdIndex = i;
        }

        // Track the most visible card overall (fallback for small viewports)
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestIndex = i;
        }
      }

      // Prefer a card that meets threshold, otherwise use most visible card
      const newIndex = bestThresholdIndex !== -1 ? bestThresholdIndex : bestIndex;

      if (newIndex !== -1) {
        setActiveCardIndex(newIndex);
      }
    };

    // Use multiple thresholds to get more granular intersection updates.
    // This ensures we detect changes even in constrained viewports.
    const thresholds = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
    const observer = new IntersectionObserver(observerCallback, { threshold: thresholds });

    for (const cardEl of cardElements) {
      if (cardEl) {
        observer.observe(cardEl);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [cardRefs, childCount, threshold, isEnabled]);

  return activeCardIndex;
};
